import React, { lazy, Suspense, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next'; // Import the translation hook
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import API from "../../API";

// Icons
import {
  calendarIcon,
  locationIcon,
  logowhiteIcon,
  longrightarrowIcon,
  logo,
} from "../../assets/images/icons";

import "./home.css";

// Lazy load components
const Carousel = lazy(() => import("../../components/Carousel/Carousel"));
const Navbar = lazy(() => import("../../components/Navbar/Navbar"));
const Topbar = lazy(() => import("../../components/Topbar/Topbar"));
const LazyTravelcard = lazy(() => import("../../components/Travelcard/Travelcard"));
const LazyHotelcard = lazy(() => import("../../components/Hotelcard/Hotelcard"));
const Partners = lazy(() => import("../../components/Partners/Partners"));
const Serviceslist = lazy(() => import("../../components/Serviceslist/Serviceslist"));
const Footer = lazy(() => import("../../components/Footer/Footer"));

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

// Loading Fallback Component
const LoadingFallback = () => (
  <div className="loading-placeholder animate-pulse">
    <div className="placeholder-content bg-gray-200"></div>
  </div>
);

// Card Grid Renderer (desktop + tablet)
const CardGrid = ({
  items,
  CardComponent,
  isVarient = false,
  keyProp = 'id',
  propName = 'voyage'
}) => {
  const placeholderCards = Math.max(0, 4 - (items || []).length);

  return (
    <motion.div className="grid" variants={fadeIn}>
      {items.map((item, index) => (
        <CardComponent
          key={item[keyProp] || index}
          isVarient={isVarient}
          {...{ [propName]: item }}
        />
      ))}
      {[...Array(placeholderCards)].map((_, index) => (
        <CardComponent key={`placeholder-${index}`} isVarient={isVarient} />
      ))}
    </motion.div>
  );
};

// One-card-at-a-time carousel for phones.
// Controls Swiper imperatively via the instance — avoids the double-binding
// bug that happens when navigation selectors resolve more than once
// (e.g. on parent re-renders) and makes each click jump two slides.
const MobileCardCarousel = ({
  items,
  CardComponent,
  isVarient = false,
  keyProp = 'id',
  propName = 'voyage',
  variant = 'voy',
}) => {
  const safeItems = Array.isArray(items) ? items : [];
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (safeItems.length === 0) return null;

  const goPrev = () => { if (swiper) swiper.slidePrev(); };
  const goNext = () => { if (swiper) swiper.slideNext(); };
  const goTo   = (i) => { if (swiper) swiper.slideTo(i); };

  return (
    <div className="mobile_card_carousel">
      <Swiper
        modules={[]}
        spaceBetween={0}
        slidesPerView={1}
        onSwiper={setSwiper}
        onSlideChange={(s) => setActiveIndex(s.activeIndex)}
        observer={true}
        observeParents={true}
      >
        {safeItems.map((item, i) => (
          <SwiperSlide key={item[keyProp] || `i-${i}`}>
            <CardComponent isVarient={isVarient} {...{ [propName]: item }} />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="mcc-controls">
        <button
          type="button"
          className="mcc-arrow"
          aria-label="Previous"
          onClick={goPrev}
          disabled={activeIndex === 0}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="mcc-pagination" role="tablist">
          {safeItems.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={activeIndex === i}
              aria-label={`Go to card ${i + 1}`}
              className={`mcc-dot ${activeIndex === i ? "is-active" : ""}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
        <button
          type="button"
          className="mcc-arrow"
          aria-label="Next"
          onClick={goNext}
          disabled={activeIndex === safeItems.length - 1}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// True when the viewport is in mobile range. Subscribes to media-query changes
// so we render either the desktop grid or the mobile carousel — never both.
const useIsMobile = (breakpoint = 480) => {
  const get = () =>
    typeof window !== "undefined" && window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
};

export default function Home() {
  const { t,i18n } = useTranslation(); // Initialize the translation hook
  const isRTL = i18n.language === 'ar';
  const isMobile = useIsMobile(480);
  const [hotels, setHotels] = useState([]);
  const [voyagenear, setVoyageNear] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const handleSearch = () => {
    if (searchInput.trim()) {
      window.location.href = `/voyage?location=${encodeURIComponent(searchInput)}`;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fourhotels, voyagefournear] = await Promise.all([
          API.get('properties/public/fourprop/latest'),
          API.get('voyage-organize/featured/latest')
          //API.get('voyage-organize/featured/ending-soon')
        ]);

        setHotels(fourhotels.data || []);
        setVoyageNear(voyagefournear.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    
    fetchData();
  }, []);

  return (
    <motion.div
      className="Page_home"
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={staggerContainer}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <h1 className="visually-hidden">Protour - Your Travel Companion</h1>
      
      <Suspense fallback={<LoadingFallback />}>
        <Topbar />
        <Navbar page="accueil" />
        <Carousel />
      </Suspense>

      <motion.div className="p1" variants={fadeIn}>
        <div className="header">
          <div className="title">
            <motion.p variants={fadeIn}>{t("home.destinations_title")}</motion.p>
          </div>
          <div className="filters">
            <motion.div 
              className="filter selected" 
              whileHover={{ scale: 1.05 }}
            >
              <p>{t("home.filter_new")}</p>
              <div className="border_bottom"></div>
            </motion.div>
          </div>
        </div>
        
        <Suspense fallback={<LoadingFallback />}>
          {isMobile ? (
            <MobileCardCarousel
              items={voyagenear}
              CardComponent={LazyTravelcard}
              isVarient={true}
              propName="voyage"
              variant="voy"
            />
          ) : (
            <CardGrid
              items={voyagenear}
              CardComponent={LazyTravelcard}
              isVarient={true}
              propName="voyage"
            />
          )}
        </Suspense>

        <motion.div className="see_more_wrapper" variants={fadeIn}>
          <motion.button
            className="see_more_btn"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => { window.location.href = '/voyage'; }}
          >
            {t("home.see_more_button")}
            <motion.img src={longrightarrowIcon} alt="→" loading="lazy" />
          </motion.button>
        </motion.div>
      </motion.div>

      <motion.div className="p2" variants={fadeIn}>
        <div className="left_side">
          <motion.p className="title" variants={fadeIn}>
            {t("home.about_title")}
          </motion.p>
          <motion.p className="text" variants={fadeIn}>
            {t("home.about_text")}
          </motion.p>
          <motion.button 
            style={{width:"140px"}} 
            onClick={() => { window.location.href = '/us' }} 
            whileHover={{ scale: 1.1 }}
          >
            {t("home.about_button")}
          </motion.button>
        </div>
        <motion.div
          className="right_side"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="logo">
            <motion.img src={logowhiteIcon} alt="logo" loading="lazy" />
          </div>
          <div className="logo2">
            <motion.img src={logo} alt="logo" loading="lazy" />
          </div>
        </motion.div>
      </motion.div>

      <motion.div className="p1" variants={fadeIn}>
        <div className="header">
          <div className="title">
            <motion.p variants={fadeIn}>
              {t("home.hotels_title")}
            </motion.p>
          </div>
          <motion.div 
            className="redirect" 
            whileHover={{ x: 10 }}
          >
            <motion.p 
              variants={fadeIn} 
              onClick={() => { window.location.href = '/hotels' }}
            >
              {t("home.see_all_button")}
            </motion.p>
            <motion.img
              src={longrightarrowIcon}
              alt={t("home.see_all_button")}
              loading="lazy"
            />
          </motion.div>
        </div>
        
        <Suspense fallback={<LoadingFallback />}>
          {isMobile ? (
            <MobileCardCarousel
              items={hotels}
              CardComponent={LazyHotelcard}
              isVarient={false}
              propName="hotel"
              keyProp="code"
              variant="hot"
            />
          ) : (
            <CardGrid
              items={hotels}
              CardComponent={LazyHotelcard}
              isVarient={false}
              propName="hotel"
              keyProp="code"
            />
          )}
        </Suspense>

        <motion.div className="see_more_wrapper" variants={fadeIn}>
          <motion.button
            className="see_more_btn"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => { window.location.href = '/hotels'; }}
          >
            {t("home.see_all_button")}
            <motion.img src={longrightarrowIcon} alt="→" loading="lazy" />
          </motion.button>
        </motion.div>
      </motion.div>

      <Suspense fallback={<LoadingFallback />}>
        <Partners />
        <Serviceslist />
        <Footer />
      </Suspense>
    </motion.div>
  );
}